import { useState, useEffect } from 'react';
import { Heart, MessageSquare, Bookmark, Eye, Pin, MoreVertical, Users, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/dialog';
import { formatDistanceToNow } from 'date-fns';
import { postsAPI, communitiesAPI } from '@/api/community';
import { useToast } from '@/hooks/use-toast';
import { getUserData } from '@/utils/auth';
import { cn } from '@/lib/utils';
import CommentThread from './CommentThread';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

export default function PostCard({ post, onUpdate, isGuardian = false }) {
  const [isLiked, setIsLiked] = useState(post.is_liked || false);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [isSaved, setIsSaved] = useState(post.is_saved || false);
  const [saveCount, setSaveCount] = useState(post.save_count || 0);
  const [viewCount, setViewCount] = useState(post.view_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const [isCommunityMember, setIsCommunityMember] = useState(post.is_community_member || false);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const { toast } = useToast();
  const userData = getUserData('student') || getUserData('teacher') || getUserData('admin') || getUserData('guardian');

  // Debug: Log author data to verify profile_frame_url is present
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && post?.author) {
      console.log('Post author data:', {
        username: post.author.username,
        profile_pic_url: post.author.profile_pic_url,
        avatar_url: post.author.avatar_url,
        profile_frame_url: post.author.profile_frame_url,
      });
    }
  }, [post]);

  useEffect(() => {
    // Track view on mount
    if (!isGuardian) {
      postsAPI.viewPost(post.id).catch(console.error);
      setViewCount(prev => prev + 1);
      try { trackEvent('community_post_view', attachUserProps({ post_id: post.id })); } catch(e) {}
    }
    // Initialize community membership state
    if (post.is_community_member !== undefined) {
      setIsCommunityMember(post.is_community_member);
    }
  }, [post.id, post.is_community_member, isGuardian]);

  const handleLike = async () => {
    if (isGuardian) return;
    
    setLoading(true);
    try {
      const response = await postsAPI.likePost(post.id);
      setIsLiked(response.is_liked);
      setLikeCount(response.like_count);
      try { trackEvent('community_post_liked', attachUserProps({ post_id: post.id, liked: response.is_liked })); } catch(e) {}
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to like post',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (isGuardian) return;
    
    setLoading(true);
    try {
      const response = await postsAPI.savePost(post.id);
      setIsSaved(response.is_saved);
      setSaveCount(response.save_count);
      try { trackEvent('community_post_saved', attachUserProps({ post_id: post.id, saved: response.is_saved })); } catch(e) {}
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save post',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };


  const handleCommentAdded = () => {
    setCommentCount(prev => prev + 1);
    if (onUpdate) onUpdate();
  };

  // Get profile frame URL
  const getProfileFrameUrl = () => {
    return post.author?.profile_frame_url || null;
  };

  // Get author avatar (priority: avatar > profile pic > null)
  // Frame is independent - if it exists, it will be displayed separately
  const getAuthorAvatar = () => {
    // Priority: avatar > profile pic
    if (post.author?.avatar_url) {
      return post.author.avatar_url;
    }
    if (post.author?.profile_pic_url) {
      return post.author.profile_pic_url;
    }
    return null;
  };

  // Get author initials
  const getAuthorInitials = () => {
    if (post.author.full_name) {
      return post.author.full_name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return post.author.username.slice(0, 2).toUpperCase();
  };

  // Get formatted author name with user type
  const getAuthorDisplayName = () => {
    const fullName = post.author.full_name || post.author.username;
    const userType = post.author.user_type;
    
    if (userType === 'teacher') {
      return `${fullName} (Teacher)`;
    } else if (userType === 'admin') {
      return `${fullName} (Admin)`;
    }
    return fullName;
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  // Check if post is leaderboard announcement (has special styling)
  const isLeaderboardAnnouncement = post.post_type === 'announcement' && 
    (post.content?.includes('rank') || post.content?.includes('leaderboard') || post.tags?.some(t => t.tag_value.toLowerCase().includes('rank')));

  return (
    <Card className={`bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] ${
      post.is_pinned ? 'border-l-4 border-l-[hsl(var(--green-bright))]' : ''
    } ${isLeaderboardAnnouncement ? 'bg-gradient-to-r from-yellow-50/10 to-orange-50/10 dark:from-yellow-950/20 dark:to-orange-950/20' : ''}`}>
      <CardContent className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {/* Avatar with Profile Frame */}
            <div className="relative w-10 h-10">
              {/* Avatar/Profile Photo - Base Layer */}
              <div className="absolute inset-0 w-full h-full rounded-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center overflow-hidden z-0" style={{ clipPath: 'circle(50% at 50% 50%)' }}>
                {getAuthorAvatar() ? (
                  <img 
                    src={getAuthorAvatar()} 
                    alt={post.author.full_name || post.author.username}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={cn(
                  "absolute inset-0 w-full h-full items-center justify-center",
                  getAuthorAvatar() ? "hidden" : "flex"
                )}>
                  <span className="text-white text-xs font-semibold">{getAuthorInitials()}</span>
                </div>
              </div>
              {/* Profile Frame Overlay - Top Layer - Larger and extending outside */}
              {getProfileFrameUrl() && (
                <div className="absolute -inset-2 rounded-full overflow-visible pointer-events-none z-10" style={{ clipPath: 'circle(50% at 50% 50%)' }}>
                  <img 
                    src={getProfileFrameUrl()} 
                    alt="Frame"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      console.error('Failed to load profile frame:', getProfileFrameUrl());
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-foreground">{getAuthorDisplayName()}</h3>
                {post.is_pinned && (
                  <Badge variant="outline" className="text-xs">
                    <Pin className="w-3 h-3 mr-1" />
                    Pinned
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {post.author.grade && (
                  <span>{post.author.grade.grade_name}</span>
                )}
                {post.author.division && (
                  <span>• {post.author.division.division_name}</span>
                )}
                <span>• {formatDate(post.created_at)}</span>
              </div>
            </div>
          </div>
          {post.post_type === 'announcement' && (
            <Badge variant="secondary" className="text-xs">
              Announcement
            </Badge>
          )}
        </div>

        {/* Content with Join Button */}
        <div className="flex items-start justify-between gap-4">
          <div 
            className="text-foreground prose prose-sm dark:prose-invert max-w-none flex-1 [&_a]:text-[hsl(var(--green-bright))] [&_a]:hover:text-[hsl(var(--teal-vivid))] [&_a]:underline [&_a]:underline-offset-2"
            dangerouslySetInnerHTML={{ 
              __html: (() => {
                // Remove @mentions and #hashtags from content since they're shown as tags separately
                if (!post.content) return '';
                
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = post.content;
                
                // Remove mention spans from HTML
                const mentions = tempDiv.querySelectorAll('span[data-type="mention"], span.mention, span[class*="mention"]');
                mentions.forEach(mention => {
                  if (mention.parentNode) {
                    mention.parentNode.removeChild(mention);
                  }
                });
                
                // Remove hashtag spans from HTML
                const hashtags = tempDiv.querySelectorAll('span[data-type="hashtagMention"], span.hashtag, span[class*="hashtag"]');
                hashtags.forEach(hashtag => {
                  if (hashtag.parentNode) {
                    hashtag.parentNode.removeChild(hashtag);
                  }
                });
                
                // Also remove from text content using regex (for plain text mentions/hashtags)
                let cleanedHtml = tempDiv.innerHTML;
                // Remove @mentions: @ followed by word characters, spaces, and numbers
                cleanedHtml = cleanedHtml.replace(/@\w+(\s+\d+)?(\s+\w+)*/g, '');
                // Remove #hashtags
                cleanedHtml = cleanedHtml.replace(/#\w+/g, '');
                
                // Clean up multiple spaces and empty paragraphs
                cleanedHtml = cleanedHtml.replace(/\s+/g, ' ').trim();
                cleanedHtml = cleanedHtml.replace(/<p>\s*<\/p>/g, '');
                
                return cleanedHtml;
              })()
            }}
          />
          {/* Community Join Button for Announcements - Right Aligned */}
          {post.post_type === 'announcement' && post.community && post.community_name && (
            <div className="flex-shrink-0">
              <Button
                onClick={async () => {
                  if (isGuardian) return;
                  try {
                    setLoading(true);
                    await communitiesAPI.joinCommunity(post.community);
                    setIsCommunityMember(true);
                    toast({
                      title: 'Success',
                      description: `Joined ${post.community_name} community!`,
                    });
                    if (onUpdate) onUpdate();
                      try { trackEvent('community_joined', attachUserProps({ community_id: post.community, post_id: post.id })); } catch(e) {}
                  } catch (error) {
                    const errorMessage = error.response?.data?.error || 'Failed to join community';
                    toast({
                      title: 'Error',
                      description: errorMessage,
                      variant: 'destructive',
                    });
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || isGuardian || isCommunityMember}
                className={isCommunityMember 
                  ? "bg-[hsl(var(--green-bright))] text-white cursor-default" 
                  : "bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))]"
                }
                size="sm"
              >
                <Users className="w-4 h-4 mr-2" />
                {isCommunityMember ? 'Joined' : 'Join'}
              </Button>
            </div>
          )}
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, idx) => (
              <Badge 
                key={idx} 
                variant="outline" 
                className="text-xs"
              >
                {tag.tag_type === 'hashtag' ? '#' : '@'}{tag.tag_value}
              </Badge>
            ))}
          </div>
        )}

        {/* Attachments */}
        {post.attachments && post.attachments.length > 0 && (
          <div className="space-y-2">
            {/* Image attachments - horizontal scroll */}
            {post.attachments.some(att => att.type?.startsWith('image/')) && (
              <div className="overflow-x-auto overflow-y-hidden">
                <div className="flex gap-2 pb-2" style={{ scrollbarWidth: 'thin' }}>
                  {post.attachments
                    .filter(att => att.type?.startsWith('image/'))
                    .map((attachment, idx) => (
                      <img
                        key={idx}
                        src={attachment.url || attachment.thumbnail_url}
                        alt={attachment.filename || 'Attachment'}
                        className="rounded-lg h-48 w-auto cursor-pointer hover:opacity-90 transition-opacity object-contain flex-shrink-0"
                        onClick={() => setSelectedImage(attachment.url || attachment.thumbnail_url)}
                      />
                    ))}
                </div>
              </div>
            )}
            {/* Non-image attachments */}
            {post.attachments
              .filter(att => !att.type?.startsWith('image/'))
              .map((attachment, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-3 bg-[hsl(var(--main-bg))] rounded-lg border border-[hsl(var(--card-border))]"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{attachment.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {(attachment.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                      onClick={() => {
                        try { trackEvent('community_attachment_download', attachUserProps({ post_id: post.id, filename: attachment.filename })); } catch(e) {}
                        window.open(attachment.url, '_blank');
                      }}
                  >
                    Download
                  </Button>
                </div>
              ))}
          </div>
        )}

        {/* Interactions */}
        <div className="flex items-center gap-6 pt-2 border-t border-[hsl(var(--card-border))]">
          <button
            onClick={handleLike}
            disabled={loading || isGuardian}
            className={`flex items-center gap-2 transition-colors ${
              isLiked
                ? 'text-red-500'
                : 'text-muted-foreground hover:text-red-500'
            } ${isGuardian ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
            <span>{likeCount}</span>
          </button>

          <button
            onClick={() => {
              const next = !showComments;
              setShowComments(next);
              try { trackEvent('community_open_comments', attachUserProps({ post_id: post.id, open: next })); } catch(e) {}
            }}
            className="flex items-center gap-2 text-muted-foreground hover:text-blue-500 transition-colors cursor-pointer"
          >
            <MessageSquare className="w-5 h-5" />
            <span>{commentCount}</span>
            {showComments ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={handleSave}
            disabled={loading || isGuardian}
            className={`flex items-center gap-2 transition-colors ${
              isSaved
                ? 'text-yellow-500'
                : 'text-muted-foreground hover:text-yellow-500'
            } ${isGuardian ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Bookmark className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
            <span>{saveCount}</span>
          </button>

          <div className="flex items-center gap-1 text-muted-foreground ml-auto">
            <Eye className="w-4 h-4" />
            <span className="text-xs">{viewCount}</span>
          </div>
        </div>

        {/* Comments */}
        {showComments && (
          <div className="pt-4 border-t border-[hsl(var(--card-border))]">
            <CommentThread 
              postId={post.id} 
              onCommentAdded={handleCommentAdded}
              isGuardian={isGuardian}
            />
          </div>
        )}
      </CardContent>

      {/* Image Preview Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0 bg-transparent border-none">
          <DialogDescription className="sr-only">Image preview</DialogDescription>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-5 h-5" />
            </Button>
            <img
              src={selectedImage || ''}
              alt="Preview"
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

            {/* Track comments open */}
            <script>{/* placeholder to keep eslint happy */}</script>
